if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedOptionValues, target),
        });
      }

     

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html, selectedOptionValues = []) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects) return;

        variantSelects.querySelectorAll('input[data-option-value-id]').forEach((input) => {
          const isSelected = selectedOptionValues.includes(input.dataset.optionValueId);
          input.checked = isSelected;
          input.toggleAttribute('checked', isSelected);
        });

        variantSelects.querySelectorAll('select').forEach((select) => {
          const selectedOption = Array.from(select.options).find((option) =>
            selectedOptionValues.includes(option.dataset.optionValueId)
          );
          select.selectedIndex = selectedOption ? selectedOption.index : -1;
        });

        HTMLUpdateUtility.viewTransition(
          this.variantSelectors,
          variantSelects,
          this.preProcessHtmlCallbacks
        );
      }

      handleUpdateProductInfo(productUrl, selectedOptionValues, changedTarget = null) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);
          const updateFallbackGallery = () => this.updateMediaForColorFallback(productUrl, changedTarget, html);

          // Preserve Dawn 16's replacement-safe variant selection flow, then apply the store's incomplete-size guard.
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);
          const optionCount = this.variantSelectors.querySelectorAll('fieldset, select').length;
          const hasCompleteSelection = selectedOptionValues.length === optionCount;
          this.pickupAvailability?.update(hasCompleteSelection ? variant : null);
          this.updateOptionValues(html, selectedOptionValues);
          this.updateURL(productUrl, hasCompleteSelection ? variant?.id : null);
          this.updateVariantInputs(hasCompleteSelection ? variant?.id : null);
          if (!hasCompleteSelection) {
            if (variant && this.variantMatchesOption(variant, changedTarget, sourceVariantSelects)) {
              this.updateMedia(html, variant.featured_media?.id);
            }
            else updateFallbackGallery();
            this.productForm?.toggleSubmitButton(false);
            return;
          }

          if (!variant) {
            this.clearNonColorOptionSelections(changedTarget);
            updateFallbackGallery();
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      /* WCF091_COLOR_GALLERY_FALLBACK_20260930 */
      clearNonColorOptionSelections(changedTarget) {
        const changedOptionName = changedTarget?.dataset?.optionName?.trim().toLowerCase();
        if (changedOptionName !== 'color') return;

        this.variantSelectors.querySelectorAll('input[data-option-name]:checked').forEach((input) => {
          const optionName = input.dataset.optionName?.trim().toLowerCase();
          if (optionName !== 'color') {
            input.checked = false;
            input.removeAttribute('checked');
          }
        });
        this.variantSelectors.querySelectorAll('select[data-option-name]').forEach((select) => {
          const optionName = select.dataset.optionName?.trim().toLowerCase();
          if (optionName !== 'color') select.selectedIndex = -1;
        });
      }

      getOptionGroups(variantSelects = this.variantSelectors) {
        return Array.from(
          variantSelects?.querySelectorAll('fieldset, .product-form__input--dropdown') || []
        ).map((group, index) => {
          const option = group.querySelector('[data-option-name]');
          return {
            index,
            name: option?.dataset.optionName?.trim().toLowerCase() || '',
          };
        });
      }

      variantMatchesOption(variant, target, variantSelects = this.variantSelectors) {
        const optionName = target?.dataset?.optionName?.trim().toLowerCase();
        const optionIndex = this.getOptionGroups(variantSelects).findIndex(({ name }) => name === optionName);
        return optionIndex >= 0 && variant?.options?.[optionIndex] === target.value;
      }

      updateMediaForColorFallback(productUrl, changedTarget, partialHtml) {
        const optionName = changedTarget?.dataset?.optionName?.trim().toLowerCase();
        const galleryVariantId = changedTarget?.dataset?.galleryVariantId;
        const colorOptionValueId = changedTarget?.dataset?.optionValueId;
        if (optionName !== 'color' || !colorOptionValueId) return;

        const separator = productUrl.includes('?') ? '&' : '?';
        const signal = this.abortController?.signal;
        const fallbackToken = Symbol('color-gallery-fallback');
        this.colorGalleryFallbackToken = fallbackToken;
        this.colorGalleryVariantCache ||= new Map();
        const cacheKey = `${this.sectionId}:${productUrl}:${colorOptionValueId}`;
        const cachedVariantId = this.colorGalleryVariantCache.get(cacheKey);
        const sizeOptionValueIds = (changedTarget.dataset.gallerySizeValueIds || '')
          .split(',')
          .filter(Boolean);
        const sizeInputs = [
          ...Array.from(
            this.variantSelectors?.querySelectorAll(
              'input[data-option-name], option[data-option-name]'
            ) || []
          ),
          ...Array.from(
            partialHtml?.querySelectorAll(
              'variant-selects input[data-option-name], variant-selects option[data-option-name]'
            ) || []
          ),
        ].filter((input) => input.dataset.optionName?.trim().toLowerCase() === 'size');
        const sizeById = new Map(sizeInputs.map((input) => [input.dataset.optionValueId, input]));
        const sizeCandidates = sizeOptionValueIds
          .map((id) => ({
            id,
            value: sizeById.get(id)?.value,
            preferred: !sizeById.get(id)?.disabled && !sizeById.get(id)?.classList.contains('disabled'),
          }))
          .filter(({ id, value }) => Boolean(id && value))
          .sort((left, right) => Number(right.preferred) - Number(left.preferred));
        const optionGroups = this.getOptionGroups(this.getVariantSelects(partialHtml));
        const colorGroup = optionGroups.find(({ name }) => name === 'color');
        const sizeGroup = optionGroups.find(({ name }) => name === 'size');
        const canProbeColorAndSize =
          optionGroups.length === 2 &&
          colorGroup &&
          sizeGroup &&
          colorGroup.index !== sizeGroup.index;
        const representativeVariantId = galleryVariantId || cachedVariantId;
        const requests = [];
        if (representativeVariantId) {
          requests.push({
            params: `variant=${encodeURIComponent(representativeVariantId)}`,
            cached: !galleryVariantId && Boolean(cachedVariantId),
          });
        }
        if (canProbeColorAndSize) {
          sizeCandidates.forEach(({ id, value }) => {
            const optionValueIds = [];
            optionValueIds[colorGroup.index] = colorOptionValueId;
            optionValueIds[sizeGroup.index] = id;
            requests.push({
              params: `option_values=${encodeURIComponent(optionValueIds.join(','))}`,
              expectedOptions: optionGroups.map(({ name }) =>
                name === 'color' ? changedTarget.value : value
              ),
            });
          });
        }

        const stillCurrentColor = () =>
          this.colorGalleryFallbackToken === fallbackToken &&
          this.variantSelectors?.selectedOptionValues?.includes(colorOptionValueId);

        const tryRequest = (index) => {
          if (!stillCurrentColor() || index >= requests.length) return Promise.resolve();

          const request = requests[index];
          const requestUrl = `${productUrl}${separator}${request.params}&section_id=${encodeURIComponent(this.sectionId)}`;
          return fetch(requestUrl, { signal })
            .then((response) => {
              if (!response.ok) {
                console.error(`Color gallery request failed (${response.status})`);
                return null;
              }
              return response.text();
            })
            .then((responseText) => {
              if (signal?.aborted || !stillCurrentColor()) return;
              if (!responseText) return tryRequest(index + 1);
              const fallbackHtml = new DOMParser().parseFromString(responseText, 'text/html');
              const fallbackVariant = this.getSelectedVariant(fallbackHtml);
              const matchesRequestedOptions = request.expectedOptions
                ? request.expectedOptions.every(
                    (value, optionIndex) => fallbackVariant?.options?.[optionIndex] === value
                  )
                : this.variantMatchesOption(
                    fallbackVariant,
                    changedTarget,
                    this.getVariantSelects(fallbackHtml)
                  );
              if (!matchesRequestedOptions) {
                if (request.cached) this.colorGalleryVariantCache.delete(cacheKey);
                return tryRequest(index + 1);
              }
              this.colorGalleryVariantCache.set(cacheKey, fallbackVariant.id);
              this.updateMedia(fallbackHtml, fallbackVariant.featured_media?.id);
            });
        };

        return tryRequest(0)
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      updateMedia(html, variantFeaturedMediaId) {

        // Replace the complete gallery when a color changes its media group.
        // This also refreshes missing thumbnail rails, slider counters and listeners.
        const mediaGallerySource = this.querySelector('media-gallery');
        const mediaGalleryDestination = html.querySelector('media-gallery');
        if (mediaGallerySource && mediaGalleryDestination) {
          const gallerySignature = (gallery) => JSON.stringify({
            media: Array.from(gallery.querySelectorAll('ul[id^="Slider-Gallery-"] > li'),
              (item) => item.dataset.mediaId),
            thumbnails: Array.from(gallery.querySelectorAll('ul[id^="Slider-Thumbnails-"] > li'),
              (item) => item.dataset.target),
          });
          if (gallerySignature(mediaGallerySource) !== gallerySignature(mediaGalleryDestination)) {
            mediaGallerySource.replaceWith(mediaGalleryDestination);
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          }
        }

        // set featured media as active in the media gallery
        if (variantFeaturedMediaId) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        // Keep the shopper's quantity when variants or cart contents refresh.
        // A newly opened product page starts from its rendered default quantity.
        const preserveProductQuantity =
          this.id.startsWith('MainProduct') &&
          Number.isInteger(this.quantityInput.valueAsNumber) &&
          this.quantityInput.valueAsNumber >= 1;
        if (!preserveProductQuantity) this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
